import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaEnvelopeOpenText, FaBook, FaCog, FaSyncAlt } from 'react-icons/fa';
import EmailMonitorTab from '../components/messaging/EmailMonitorTab';
import KnowledgeTab from '../components/messaging/KnowledgeTab';
import EmailMonitorSettingsModal from '../components/messaging/EmailMonitorSettingsModal';
import {
  refreshMonitoredEmails,
  getEmailMonitorSettings,
} from '../services/emailMonitorService';
import { useAppConfig } from '../context/AppConfigContext';

// Email Monitor module, split out of Messaging: inbound support mail plus the
// reply-knowledge library that grounds its drafts. Wide and tall on purpose —
// the monitor is a workbench, not a form, so it takes the space available.
//
// The page owns the header row (title + settings + "Check mail"), the banner,
// and the monitor settings; the tabs below it own their own lists.

const TABS = [
  { key: 'email', label: 'Email Monitor', Icon: FaEnvelopeOpenText },
  { key: 'knowledge', label: 'Knowledge', Icon: FaBook },
];
const DEFAULT_TAB = 'email';

const EmailMonitorPage = () => {
  // The URL is the single source of truth for the tab: back/forward works and a
  // filtered view is linkable. Old ?tab=email bookmarks keep working.
  const [searchParams, setSearchParams] = useSearchParams();
  const { config } = useAppConfig();

  const requestedTab = searchParams.get('tab');
  const tab = TABS.some((t) => t.key === requestedTab) ? requestedTab : DEFAULT_TAB;

  const [mailboxes, setMailboxes] = useState([]);
  const [lookbackHours, setLookbackHours] = useState(24);
  const [checking, setChecking] = useState(false);
  const [banner, setBanner] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Bumped by "Check mail" so the monitor tab re-reads what the poll just found.
  const [refreshToken, setRefreshToken] = useState(0);

  // Settings give us the mailbox list for the filter and the window the poll uses.
  const loadSettings = useCallback(async () => {
    try {
      const s = await getEmailMonitorSettings();
      setMailboxes(s?.mailboxes || []);
      if (Number.isFinite(Number(s?.lookbackHours))) setLookbackHours(Number(s.lookbackHours));
    } catch {
      // Non-fatal: the list still works, the mailbox filter just falls back to what the emails show.
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const selectTab = useCallback((next) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      // The default tab carries no param, matching how InvoicesPage keeps the canonical URL clean.
      if (next === DEFAULT_TAB) params.delete('tab');
      else params.set('tab', next);
      // Dropping it when leaving the monitor so a stale email cannot reopen elsewhere.
      if (next !== 'email') params.delete('e');
      return params;
    });
  }, [setSearchParams]);

  // Polls Microsoft right now rather than waiting for the 5-minute schedule. Slow
  // by nature — it is doing the real Graph round trip — so the button shows progress.
  const checkForNewMail = async () => {
    setChecking(true);
    setBanner(null);
    try {
      const result = await refreshMonitoredEmails();
      const found = Number(result?.processed || 0);
      setBanner({
        type: 'success',
        text: found > 0 ? `Checked the mailboxes: ${found} new email(s).` : 'Checked the mailboxes: nothing new.',
      });
      setRefreshToken((n) => n + 1);
    } catch (e) {
      setBanner({ type: 'error', text: e.message || 'Could not check for new mail.' });
    } finally {
      setChecking(false);
    }
  };

  if (config && config.inboxMonitorEnabled !== true) {
    return (
      <div className="mx-auto px-4 py-6 max-w-[110rem]">
        <h1 className="text-2xl font-bold text-jims-cyan-dark flex items-center gap-3 mb-4">
          <FaEnvelopeOpenText /> Email Monitor
        </h1>
        <div className="px-4 py-3 rounded border bg-amber-50 border-amber-200 text-amber-800">
          Email monitoring is currently <b>switched off</b> on the server (INBOX_MONITOR_ENABLED).
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 max-w-[110rem]">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-jims-cyan-dark flex items-center gap-3">
          <FaEnvelopeOpenText /> Email Monitor
        </h1>
        {tab === 'email' && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              aria-label="Email monitor settings"
              className="p-2 text-gray-500 hover:text-gray-800 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan"
            >
              <FaCog />
            </button>
            <button
              type="button"
              onClick={checkForNewMail}
              disabled={checking}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-jims-cyan rounded hover:bg-jims-cyan-dark disabled:opacity-50"
            >
              <FaSyncAlt className={checking ? 'animate-spin' : ''} />
              {checking ? 'Checking…' : 'Check mail'}
            </button>
          </div>
        )}
      </div>

      <div className="border-b border-gray-200 mb-4" role="tablist" aria-label="Email monitor view">
        <nav className="-mb-px flex gap-6">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => selectTab(key)}
              className={`flex items-center gap-2 py-2.5 px-1 text-sm font-medium border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-jims-cyan rounded-t ${
                tab === key
                  ? 'border-jims-cyan text-jims-cyan-dark'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon /> {label}
            </button>
          ))}
        </nav>
      </div>

      {banner && (
        <div className={`mb-4 px-4 py-3 rounded border flex justify-between items-start ${
          banner.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <span>{banner.text}</span>
          <button onClick={() => setBanner(null)} className="ml-4 text-sm opacity-70 hover:opacity-100">Dismiss</button>
        </div>
      )}

      {tab === 'email' && (
        <EmailMonitorTab
          refreshToken={refreshToken}
          mailboxes={mailboxes}
          lookbackHours={lookbackHours}
          notify={setBanner}
        />
      )}
      {tab === 'knowledge' && <KnowledgeTab />}

      <EmailMonitorSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={(saved) => {
          setMailboxes(saved?.mailboxes || mailboxes);
          if (Number.isFinite(Number(saved?.lookbackHours))) setLookbackHours(Number(saved.lookbackHours));
          setBanner({ type: 'success', text: 'Settings saved.' });
        }}
      />
    </div>
  );
};

export default EmailMonitorPage;
